import MultiSelect from "components/common/MultiSelect";
import Switch from "components/common/Switch";
import CustomField from "components/form/CustomField";
import DateTimePicker from "components/form/DateTimePicker";
import { Form, Formik } from "formik";
import { appendFormData, handleSubmitForm } from "helpers/apiUtils";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { formatOptionsForSelect } from "utils/formatOptions";
import { AddCouponSchema } from "utils/validations";
import { MultiSelect as PrimeMultiSelect } from "primereact/multiselect";
import { getCustomersList } from "store/slices/customersSlice";
import { useQueryParam } from "hooks/useQueryParam";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import PageHeader from "components/common/PageHeader";
import SingleSelect from "components/common/SingleSelect";
import { COUPON_DISCOUNT_TYPE, PLAN_OPTIONS } from "utils/constants";
import {
  addCoupon,
  getCouponDetails,
  updateCoupon,
} from "store/slices/couponSlice";
import { getPlansList } from "store/slices/membershipSlice";
import FileUpload from "components/common/FileUpload";

const AddEditCoupon = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const isEdit = mode === "edit";
  const id = useQueryParam("id");

  const {
    isLoading,
    loading,
    itemDetails: couponDetails,
  } = useSelector((state) => state.Coupons);
  const { customersList } = useSelector((state) => state.Customers);
  const plansList = useSelector((state) => state.Membership.plansList);

  const [customerOptions, setCustomerOptions] = useState([]);
  const [initialValues, setInitialValues] = useState({
    couponCode: "",
    discountType: "",
    visibility: "",
    isPrivate: false,
    isUserRestricted: false,
    discountAmount: "",
    usageRules: {
      hasGlobalUsageLimit: false,
      maxTotalRedemptions: 0, // Added this
      hasPerUserUsageLimit: false,
      maxRedemptionsPerUser: 0, // Added this
      hasMaxDiscountLimit: false,
      maxDiscountAmount: 0,
    },
    neverEnds: false,
    startsAt: new Date(),
    endsAt: "",
    allowedUsers: [],
    offerDetails: {
      title: { en: "", ar: "" },
      description: { en: "", ar: "" },
    },
    planTypes: [],
    plans: [],
    offerImage: null,
  });

  useEffect(() => {
    dispatch(getCustomersList(""));

    if (isEdit && id) {
      dispatch(getCouponDetails(id));
    }
  }, [isEdit, id, dispatch]);

  useEffect(() => {
    if (isEdit && couponDetails) {
      const {
        couponCode,
        visibility,
        isPrivate,
        isUserRestricted,
        discountType,
        discountAmount,
        allowedUsers,
        startsAt,
        endsAt,
        neverEnds,
        usageRules,
        offerDetails,
        planTypes,
        plans,
        offerImage,
      } = couponDetails || {};

      // CHANGED: Extract IDs directly if allowedUsers contains objects or strings
      const selectedIds = Array.isArray(allowedUsers)
        ? allowedUsers.map((c) => (typeof c === "string" ? c : c._id))
        : [];

      setInitialValues({
        couponCode: couponCode || "",
        visibility: visibility || "",
        isPrivate: isPrivate || false,
        isUserRestricted: isUserRestricted || false,
        discountType: discountType || "",
        discountAmount: discountAmount || "",
        usageRules: {
          hasGlobalUsageLimit: usageRules?.hasGlobalUsageLimit || false,
          maxTotalRedemptions: usageRules?.maxTotalRedemptions || 0,
          hasPerUserUsageLimit: usageRules?.hasPerUserUsageLimit || false,
          maxRedemptionsPerUser: usageRules?.maxRedemptionsPerUser || 0,
          hasMaxDiscountLimit: usageRules?.hasMaxDiscountLimit || false,
          maxDiscountAmount: usageRules?.maxDiscountAmount || 0,
        },
        neverEnds: neverEnds,
        startsAt: startsAt ? new Date(startsAt) : "",
        endsAt: endsAt ? new Date(endsAt) : "",
        allowedUsers: selectedIds,
        offerDetails: {
          title: {
            en: offerDetails?.title?.en || "",
            ar: offerDetails?.title?.ar || "",
          },
          description: {
            en: offerDetails?.description?.en || "",
            ar: offerDetails?.description?.ar || "",
          },
        },
        planTypes: planTypes || [],
        plans: plans || [],
        offerImage: offerDetails?.imageUrl || null,
      });

      const resolvedPlanTypes = planTypes || [];
      if (resolvedPlanTypes.length === 1) {
        dispatch(getPlansList({ planType: resolvedPlanTypes[0] }));
      } else {
        dispatch(getPlansList()); // fetch all if both or none
      }

      // CHANGED: Only set customer options if allowedUsers has full objects with names
      if (allowedUsers?.length && allowedUsers[0]?._id) {
        setCustomerOptions(
          allowedUsers.map(({ _id, name, phone }) => ({
            name: `${phone} ${name || ""}`,
            value: _id,
          })),
        );
      }
    }
  }, [isEdit, couponDetails]);

  useEffect(() => {
    if (customersList?.length) {
      setCustomerOptions((prev) => {
        const map = new Map(prev.map((o) => [o.value, o]));

        customersList.forEach(({ _id, name, phone }) => {
          map.set(_id, {
            name: `${phone} ${name || ""}`,
            value: _id,
          });
        });

        return Array.from(map.values());
      });
    }
  }, [customersList]);

  const handleSubmit = (values, { resetForm }) => {
    const toUTCISOString = (date) => date.toISOString();

    const {
      offerImage,
      endsAt,
      neverEnds,
      planTypes,
      plans,
      startsAt,
      ...rest
    } = values;
    const payload = {
      ...rest,
      discountAmount: Number(values.discountAmount),
      usageRules: {
        hasGlobalUsageLimit: values.usageRules.hasGlobalUsageLimit,
        maxTotalRedemptions: values.usageRules.hasGlobalUsageLimit
          ? Number(values.usageRules.maxTotalRedemptions) || 0
          : 0,
        hasPerUserUsageLimit: values.usageRules.hasPerUserUsageLimit,
        maxRedemptionsPerUser: values.usageRules.hasPerUserUsageLimit
          ? Number(values.usageRules.maxRedemptionsPerUser) || 0
          : 0,
        hasMaxDiscountLimit: values.usageRules.hasMaxDiscountLimit,
        maxDiscountAmount: values.usageRules.hasMaxDiscountLimit
          ? Number(values.usageRules.maxDiscountAmount) || 0
          : 0,
      },
      plans,
      planTypes,
      neverEnds,
      startsAt: startsAt ? toUTCISOString(startsAt) : "",
    };
    if (!neverEnds) {
      payload.endsAt = endsAt ? toUTCISOString(endsAt) : "";
    }

    // ✅ Same approach as AddEditBranch
    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      appendFormData(data, key, value);
    });

    // ✅ Handle offerImage — single file
    if (offerImage instanceof File) {
      // Newly uploaded file
      data.append("offerImage", offerImage);
    } else if (typeof offerImage === "string") {
      // Existing URL from API (edit mode)
      data.append("offerImage", offerImage);
    }
    if (!plans?.length) data.append("plans[]", "");
    if (!planTypes?.length) data.append("planTypes[]", "");

    if (isEdit) {
      data.append("id", id);
    }

    handleSubmitForm({
      dispatch,
      action: isEdit ? updateCoupon : addCoupon,
      payload: data,
      onSuccess: () => {
        resetForm();
        goBack();
      },
    });
  };

  if (isEdit && loading)
    return <AddEditLoader title={t("Edit Coupon")} rows={3} cols={3} />;

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn={isEdit ? true : false}
        title={isEdit ? t("Edit Coupon") : t("Add Coupon")}
      />
      <div className="common_card add_edit_coupon">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={AddCouponSchema(t)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Code")}
                      name="couponCode"
                      placeholder={t("10%OFF")}
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <SingleSelect
                      required
                      label={t("Discount Type")}
                      name={`discountType`}
                      options={COUPON_DISCOUNT_TYPE}
                      value={values?.discountType}
                      handleSingleSelectChange={(opt) =>
                        setFieldValue(`discountType`, opt.value)
                      }
                      error={
                        errors.discountType && touched.discountType ? errors.discountType : null
                      }
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      type="number"
                      label={
                        values?.discountType === "percentage"
                          ? t("Enter Percentage")
                          : t("Discount Amount")
                      }
                      placeholder={
                        values?.discountType === "percentage"
                          ? t("10%")
                          : t("10 SAR")
                      }
                      name="discountAmount"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <Switch
                      label={t("Private Offer")}
                      infoText={t("This will not be visible to all customers and can only be accessed under specific conditions")}
                      minWidth={100}
                      checked={values?.isPrivate}
                      handleOnChange={() =>
                        setFieldValue(`isPrivate`, !values.isPrivate)
                      }
                    />
                  </div>
                </Col>
                <hr className="mt-3" />
                <h5 className="mb-3">{t("Usage Rules")}</h5>
                <Col lg={3}>
                  <div className="form_field">
                    <Switch
                      label={t("Enable Global Usage Limit")}
                      minWidth={100}
                      checked={values?.usageRules?.hasGlobalUsageLimit}
                      handleOnChange={() =>
                        setFieldValue(
                          `usageRules.hasGlobalUsageLimit`,
                          !values?.usageRules?.hasGlobalUsageLimit,
                        )
                      }
                    />
                  </div>
                </Col>
                {values?.usageRules?.hasGlobalUsageLimit && (
                  <Col lg={3}>
                    <div className="form_field">
                      <CustomField
                        type="number"
                        label={t("Maximum Total Uses")}
                        infoText={t("Maximum number of times this can be used overall.")}
                        name="usageRules.maxTotalRedemptions" // FIXED: was hasGlobalUsageLimit
                        placeholder={t("Enter Max Redemptions")}
                      />
                    </div>
                  </Col>
                )}
                <Col lg={3}>
                  <div className="form_field">
                    <Switch
                      label={t("Enable Per Customer Usage Limit")}
                      minWidth={100}
                      checked={values?.usageRules?.hasPerUserUsageLimit}
                      handleOnChange={() =>
                        setFieldValue(
                          `usageRules.hasPerUserUsageLimit`,
                          !values?.usageRules?.hasPerUserUsageLimit,
                        )
                      }
                    />
                  </div>
                </Col>
                {values?.usageRules?.hasPerUserUsageLimit && (
                  <Col lg={3}>
                    <div className="form_field">
                      <CustomField
                        type="number"
                        label={t("Per Customer Redemption Limit")}
                        infoText={t("Maximum number of times a single user can use this.")}
                        name="usageRules.maxRedemptionsPerUser"
                        placeholder={t("Enter Per Customer Redemption Limit")}
                      />
                    </div>
                  </Col>
                )}
                {/* <Col lg={3}>
                  <div className="form_field">
                    <Switch
                      label={t("Has Max Discount Limit")}
                      minWidth={100}
                      checked={values?.usageRules?.hasMaxDiscountLimit}
                      handleOnChange={() =>
                        setFieldValue(
                          `usageRules.hasMaxDiscountLimit`,
                          !values?.usageRules?.hasMaxDiscountLimit,
                        )
                      }
                    />
                  </div>
                </Col> */}
                {/* {values?.usageRules?.hasMaxDiscountLimit && (
                  <Col lg={3}>
                    <div className="form_field">
                      <CustomField
                        type="number"
                        label={t("Max Discount Amount")}
                        name="usageRules.maxDiscountAmount"
                        placeholder={t("Enter Max Amount")}
                      />
                    </div>
                  </Col>
                )} */}

                <hr className="mt-3" />

                <Col lg={3}>
                  <div className="form_field">
                    <Switch
                      label={t("Never Expires")}
                      infoText={t("If enabled, this will not have an end date.”")}
                      minWidth={100}
                      checked={values?.neverEnds}
                      handleOnChange={() =>
                        setFieldValue(`neverEnds`, !values?.neverEnds)
                      }
                    />
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="form_field w-100">
                    <DateTimePicker
                      label={t("Start Date & Time")}
                      required
                      value={values.startsAt || null}
                      onChange={(e) => {
                        setFieldValue("startsAt", e);
                      }}
                      hourFormat="12"
                      minDate={new Date()}
                      error={
                        errors?.startsAt && touched?.startsAt
                          ? errors?.startsAt
                          : null
                      }
                    />
                  </div>
                </Col>
                {!values?.neverEnds && (
                  <Col lg={3}>
                    <div className="form_field w-100">
                      <DateTimePicker
                        label={t("End Date & Time")}
                        required
                        value={values.endsAt}
                        onChange={(e) => {
                          setFieldValue("endsAt", e);
                        }}
                        hourFormat="12"
                        minDate={new Date()}
                      />
                      {errors?.endsAt && touched?.endsAt && (
                        <div className="text-danger">{errors?.endsAt}</div>
                      )}
                    </div>
                  </Col>
                )}
                <Col lg={6}>
                  <div className="form_field">
                    <Switch
                      label={t("Restrict to Specific Customers")}
                      infoText={t("Only specific customers will be able to use this.")}
                      minWidth={100}
                      checked={values?.isUserRestricted}
                      handleOnChange={(e) => {
                        setFieldValue(
                          `isUserRestricted`,
                          !values.isUserRestricted,
                        );
                        if (!values.isUserRestricted) {
                        }
                      }}
                    />
                  </div>
                </Col>
                {values.isUserRestricted && (
                  <Col lg={6}>
                    <div className="form_field">
                      <label>
                        {t("Customers")}
                        <span className="text-danger">*</span>
                      </label>
                      <PrimeMultiSelect
                        value={values?.allowedUsers || []}
                        options={customerOptions}
                        onChange={(e) => setFieldValue("allowedUsers", e.value)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder={t("Select Customers")}
                        display="comma"
                        maxSelectedLabels={3}
                        filter={true}
                        onFilter={(e) => dispatch(getCustomersList(e.filter))}
                        className={`custom_multiselect`}
                      />
                    </div>
                  </Col>
                )}
                <hr className="mt-3" />

                <Col lg={6}>
                  <div className="form_field">
                    <MultiSelect
                      label={t("Plan Types")}
                      name="planTypes"
                      value={values?.planTypes || []}
                      options={PLAN_OPTIONS}
                      placeholder={t("Select Plan Types")}
                      onChange={(val) => {
                        setFieldValue("planTypes", val);

                        if (val?.length === 1) {
                          dispatch(getPlansList({ planType: val[0] }));

                          // ✅ Remove plans whose planType is NOT in the new selected types
                          const filteredSelected = values.plans.filter(
                            (planId) => {
                              const plan = plansList.find(
                                (p) => p._id === planId,
                              );
                              return plan?.planType === val[0]; // keep only matching type
                            },
                          );
                          setFieldValue("plans", filteredSelected);
                        } else if (val?.length > 1) {
                          dispatch(getPlansList()); // fetch all
                          // ✅ Both selected = keep everything, no filtering needed
                        } else {
                          // nothing selected = clear plans
                          setFieldValue("plans", []);
                        }
                      }}
                      className={
                        errors?.planTypes && touched?.planTypes
                          ? "is-invalid"
                          : ""
                      }
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <MultiSelect
                      label={t("Plans")}
                      value={values.plans}
                      options={formatOptionsForSelect(plansList, language)}
                      onChange={(val) => {
                        setFieldValue("plans", val);
                      }}
                      error={
                        errors.planId && touched.planId ? errors.planId : null
                      }
                    />
                  </div>
                </Col>
                <hr className="mt-3" />
                <h5 className="mb-3">{t("Offer Details")}</h5>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Offer Title (English)")}
                      name="offerDetails.title.en"
                      placeholder={t("Enter Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="offerDetails.title.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Offer Title (Arabic)")}
                      name="offerDetails.title.ar"
                      placeholder={t("Enter Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="offerDetails.title.en"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (English)")}
                      name="offerDetails.description.en"
                      placeholder={t("Enter Description (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="offerDetails.description.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (Arabic)")}
                      name="offerDetails.description.ar"
                      placeholder={t("Enter Description (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="offerDetails.description.en"
                      required
                    />
                  </div>
                </Col>

                <Col lg={12}>
                  <FileUpload
                    label={t("Offer Image")}
                    uploadedFiles={values.offerImage ? [values.offerImage] : []} // ✅ wrap in array for component display
                    setUploadedFiles={(file) =>
                      setFieldValue("offerImage", file?.[0] || null)
                    }
                    multiple={true}
                    maxFiles={1}
                    maxFileSize={0.6 * 1024 * 1024}
                    acceptedFileTypes={["image/png", "image/jpeg"]}
                    required={false}
                    className={
                      touched?.images && errors.images ? "invalid" : ""
                    }
                    error={errors.images}
                  />
                </Col>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Edit Coupon") : t("Add Coupon")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditCoupon;
